import React from 'react';
import { motion } from 'framer-motion';
import { getColor } from '@/lib/icons';
import api from '@/lib/api';
import { AppIcon } from '@/components/AppIcon';
import { HealthDot } from '@/components/HealthDot';
import { ExternalLink, Link2 } from 'lucide-react';
import { toast } from 'sonner';

export function AppCard({ app, index = 0, health }) {
  const color = getColor(app.color);

  const launch = (e) => {
    if (e) e.stopPropagation();
    if (!app.url) {
      toast.error('No URL configured for this app yet.');
      return;
    }
    // Open the tool in its own window, then record the open for the access log.
    window.open(app.url, '_blank', 'noopener,noreferrer');
    if (app.id) api.post(`/apps/${app.id}/open`).catch(() => {});
  };

  const copyLink = (e) => {
    e.stopPropagation();
    const link = `${window.location.origin}/${app.slug}`;
    navigator.clipboard?.writeText(link);
    toast.success('Shortcut link copied', { description: `/${app.slug}` });
  };

  const status = health?.status || 'checking';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.3), ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -3 }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={launch}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); launch(); } }}
        data-testid="app-card"
        data-slug={app.slug}
        title={`Open ${app.name} in a new window`}
        className="group relative overflow-hidden rounded-2xl border border-[color:var(--cx-border)] bg-[color:var(--cx-surface)] backdrop-blur-xl shadow-[var(--cx-shadow-soft)] transition-colors duration-200 hover:border-[rgba(46,230,197,0.4)] hover:shadow-[var(--cx-shadow-glow)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--cx-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--cx-bg)] cursor-pointer"
      >
        <div className="h-1.5 w-full" style={{ background: `linear-gradient(90deg, ${color.from}, ${color.to})` }} />
        <div className="p-5 md:p-6">
          <div className="flex items-start justify-between">
            <AppIcon app={app} size={110} iconClass="h-12 w-12" />
            <div className="flex items-center gap-2">
              <HealthDot status={status} latency={health?.latency_ms} showLabel />
              <ExternalLink className="h-4 w-4 text-[color:var(--cx-muted)] opacity-0 -translate-y-1 translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-all duration-200" />
            </div>
          </div>

          <h3 className="mt-4 font-display text-lg tracking-tight text-[color:var(--cx-fg)]">{app.name}</h3>
          <p className="mt-1 text-sm text-[color:var(--cx-fg-muted)] line-clamp-2 min-h-[2.5rem]">
            {app.description || 'No description yet.'}
          </p>

          <div className="mt-4 flex items-center justify-between">
            <span className="font-mono text-xs text-[color:var(--cx-muted)]">/{app.slug}</span>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
              <button onClick={copyLink} title="Copy shortcut link" data-testid="app-card-copy-button"
                className="h-8 w-8 rounded-lg flex items-center justify-center text-[color:var(--cx-muted)] hover:text-[color:var(--cx-fg)] hover:bg-[color:var(--cx-surface-solid)] transition-colors">
                <Link2 className="h-4 w-4" />
              </button>
              <button onClick={launch} title="Open in a new window" data-testid="app-card-open-new-tab-button"
                className="h-8 w-8 rounded-lg flex items-center justify-center text-[color:var(--cx-muted)] hover:text-[color:var(--cx-fg)] hover:bg-[color:var(--cx-surface-solid)] transition-colors">
                <ExternalLink className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
